import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { reviews } from '@/mocks/reviews';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9qtj6cnlsngrm4lm5qg';

const milestones = [
  { year: '2021', title: '平台正式上线', description: '黄瓜视频1.0版本正式发布，首月注册用户突破100万。' },
  { year: '2022', title: '用户突破千万', description: '平台用户数突破1000万，日活跃用户超过300万，进入快速增长期。' },
  { year: '2023', title: '创作者生态完善', description: '发布创作者激励计划，入驻创作者突破10万，多条视频播放量破亿。' },
  { year: '2024', title: 'AI赋能创作', description: '推出AI智能剪辑工具，大幅降低创作门槛，注册用户突破3000万。' },
  { year: '2025', title: '全球化布局', description: '进军海外市场，支持多语言版本，创作者突破50万，成为行业领先平台。' },
  { year: '2026', title: '打造内容生态', description: '注册用户突破5000万，日均播放量超10亿，构建完整的内容创作与消费生态。' },
];

const stats = [
  { label: '注册用户', value: '5000万+' },
  { label: '活跃创作者', value: '100万+' },
  { label: '日均播放量', value: '10亿+' },
  { label: '覆盖国家', value: '150+' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!CONTACT_FORM_URL) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后再试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 黄瓜视频"
        description="黄瓜视频品牌介绍、发展历程、用户口碑与联系方式。了解黄瓜视频的使命与愿景，加入我们的创作者社区。"
        keywords="黄瓜视频,关于黄瓜视频,短视频平台介绍,黄瓜视频联系方式"
        canonicalUrl="https://huanggua.video/about"
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于黄瓜视频',
            description: '黄瓜视频品牌介绍、发展历程与联系方式',
            url: 'https://huanggua.video/about',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '黄瓜视频',
            url: 'https://huanggua.video',
            description: '黄瓜视频是一款专注于短视频创作、分享与发现的平台',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+86-400-888-0000',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative py-16 md:py-24 overflow-hidden bg-background-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20orange%20gradient%20with%20flowing%20digital%20shapes%2C%20modern%20minimalist%20tech%20background%2C%20creative%20platform%20theme%2C%20soft%20geometric%20patterns%2C%20clean%20and%20professional%20atmosphere%2C%20dark%20rich%20tones&width=1920&height=600&seq=about-hero-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-foreground-950/70"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
              关于黄瓜视频
            </h1>
            <p className="text-background-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              黄瓜视频成立于2021年，是一家专注于短视频创作、分享与发现的创新型平台。我们致力于让每一个人都能轻松创作和分享自己的视频内容，用影像记录生活、表达自我、连接世界。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 bg-background-50 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              品牌使命与愿景
            </h2>
            <p className="text-foreground-600 text-sm text-center max-w-2xl mx-auto mb-12">
              用科技赋能创意，让每一个好内容都被世界看见
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="rounded-xl bg-background-100/70 border border-background-200/60 p-6">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-focus-2-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让每一个人都能轻松记录和分享生活中的精彩瞬间。无论你是专业创作者还是普通用户，黄瓜视频都为你提供最简单易用的工具和最广阔的表达舞台。我们相信，每个人都有值得被看见的故事。
                </p>
              </div>

              <div className="rounded-xl bg-background-100/70 border border-background-200/60 p-6">
                <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-rocket-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打造全球领先的短视频创作与分享生态。我们希望连接创作者与观众，构建一个充满活力、尊重原创、鼓励创新的内容社区。通过持续的技术创新，让创作变得更智能、更高效、更有趣。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 md:py-24 bg-background-100/60">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              发展历程
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-12">
              从创立到行业领先，每一步都走得扎实
            </p>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-px md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:block flex-1"></div>
                    <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 border-4 border-background-100">
                      <div className="w-2 h-2 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 bg-background-50 rounded-xl border border-background-200/60 p-5">
                      <span className="text-primary-600 font-bold text-sm">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600 mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              用户口碑
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-12">
              听听创作者们怎么说
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="rounded-xl bg-background-100/70 border border-background-200/60 p-5"
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${
                          i < review.rating
                            ? 'ri-star-fill text-amber-400'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4 line-clamp-3">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-24 bg-background-100/60">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-12">
              有任何问题或合作意向，欢迎随时联系我们
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                <p className="text-xs text-foreground-500">support@huanggua.video</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                <p className="text-xs text-foreground-500">400-888-0000</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                <p className="text-xs text-foreground-500">北京市朝阳区科技园区黄瓜大厦</p>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8">
              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formStatus === 'error' && formError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-600">{formError}</p>
                  </div>
                )}

                {formStatus === 'success' && (
                  <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
                    <p className="text-sm text-green-600">提交成功！我们会尽快与您联系。</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}